/**
 * iter-230l — Admin partial-refund modal.
 *
 * Lives ONLY on the Admin view of OrderDetailsNew. Vendors never see
 * this button — refund execution is super_admin gated end-to-end
 * (frontend + backend).
 *
 * Flow:
 *   1. Open the modal → load order line items, group by vendor.
 *   2. Admin ticks lines + sets qty, picks shipping mode, types reason
 *      (≥20 chars).
 *   3. "Preview" hits POST /api/admin/refunds/orders/:id/preview which
 *      calls Shopify /refunds/calculate.json — NO money moves.
 *   4. Preview card renders Shopify's authoritative breakdown.
 *   5. Admin types their password to confirm (re-auth gate) and clicks
 *      "Refund ₹X" → POST /execute. This is the irreversible step.
 *
 * The button label always shows the Shopify-calculated amount so the
 * admin never has to compute anything client-side.
 */
import React, { useState, useMemo } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { AlertTriangle, Loader2, CheckCircle2, Info } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import MultiVendorReversalDialog from './MultiVendorReversalDialog';

const RefundModal = ({ order, open, onOpenChange, onRefunded }) => {
  const { apiCall, user } = useAuth();
  const { toast } = useToast();

  const lines = useMemo(() => order?.line_items || order?.items || [], [order]);

  // Vendor name: try line-item field first, fall back to order-level vendor info
  const orderVendorName = useMemo(() =>
    order?.vendor?.name || order?.vendor_name || (typeof order?.vendor === 'string' ? order.vendor : null) ||
    (order?.vendor_id ? `Vendor …${String(order.vendor_id).slice(-6)}` : null),
  [order]);

  // Selection state — keyed by line_item_id
  // `amount` is the admin-specified partial refund amount (INR string).
  // Defaults to qty * unit_price when the line is first checked.
  const initialSelection = useMemo(() => {
    const s = {};
    lines.forEach((li) => {
      const lid = String(li.shopify_line_item_id || li.id || li.line_item_id);
      s[lid] = { selected: false, quantity: 1, restock_type: 'no_restock', amount: '', meta: li };
    });
    return s;
  }, [lines]);
  const [selection, setSelection] = useState(initialSelection);
  const [shippingMode, setShippingMode] = useState('proportional');
  const [shippingCustomAmount, setShippingCustomAmount] = useState('');
  const [reason, setReason] = useState('');
  const [notify, setNotify] = useState(true);
  const [reversePayout, setReversePayout] = useState(true);

  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);

  const [reauthPwd, setReauthPwd] = useState('');
  const [executing, setExecuting] = useState(false);
  const [executeResult, setExecuteResult] = useState(null);
  // iter-232 Phase 9 — open the per-vendor reversal sub-dialog when
  // the broad refund returns `needs_manual_reversal` (multi-vendor partial).
  const [mvrOpen, setMvrOpen] = useState(false);

  // Reset state when opening a new order or after a refund
  React.useEffect(() => {
    if (!open) return;
    setSelection(initialSelection);
    setPreview(null);
    setPreviewError(null);
    setExecuteResult(null);
    setReauthPwd('');
    setReason('');
    setShippingCustomAmount('');
  }, [open, initialSelection]);

  // ─────── Group lines by vendor ───────
  const groupedByVendor = useMemo(() => {
    const groups = {};
    lines.forEach((li) => {
      const v = li.vendor_name || (typeof li.vendor === 'string' ? li.vendor : li.vendor?.name) || orderVendorName || 'Unknown vendor';
      if (!groups[v]) groups[v] = [];
      groups[v].push(li);
    });
    return groups;
  }, [lines, orderVendorName]);

  const selectedLineIds = Object.entries(selection)
    .filter(([, v]) => v.selected)
    .map(([k]) => k);

  const canPreview = selectedLineIds.length > 0;
  const canExecute =
    preview && reason.trim().length >= 20 && reauthPwd.length >= 4 && !executing;
  // iter-refund-cta-clarity — the Refund button silently stays disabled
  // until ALL of reason/password/preview are satisfied, with no visual
  // cue as to which one is missing. Surface the exact blocker(s) so an
  // admin who typed a password but not a long-enough reason (or vice
  // versa) isn't left thinking the password field itself is broken.
  const executeBlockers = !preview
    ? []
    : [
        reason.trim().length < 20 && `Reason needs ${20 - reason.trim().length} more character(s)`,
        reauthPwd.length < 4 && 'Enter your password (min 4 characters) to confirm',
      ].filter(Boolean);

  const toggleLine = (lid) => {
    setSelection((prev) => {
      const row = prev[lid];
      const nowSelected = !row.selected;
      // Pre-fill amount with qty × unit_price when first checking a line
      let defaultAmount = row.amount;
      if (nowSelected && !defaultAmount) {
        const li = row.meta;
        const unitPrice = parseFloat(li?.price || li?.unit_price || 0);
        if (unitPrice > 0) defaultAmount = (unitPrice * row.quantity).toFixed(2);
      }
      return { ...prev, [lid]: { ...row, selected: nowSelected, amount: defaultAmount } };
    });
    setPreview(null);
  };

  const setQty = (lid, qty) => {
    setSelection((prev) => {
      const row = prev[lid];
      const newQty = Math.max(1, parseInt(qty || '1', 10));
      // Re-calculate amount if it was auto-filled (not manually edited)
      let newAmount = row.amount;
      const li = row.meta;
      const unitPrice = parseFloat(li?.price || li?.unit_price || 0);
      const prevAutoAmount = unitPrice > 0 ? (unitPrice * row.quantity).toFixed(2) : '';
      if (row.amount === prevAutoAmount && unitPrice > 0) {
        newAmount = (unitPrice * newQty).toFixed(2);
      }
      return { ...prev, [lid]: { ...row, quantity: newQty, amount: newAmount } };
    });
    setPreview(null);
  };

  const setAmount = (lid, val) => {
    setSelection((prev) => ({ ...prev, [lid]: { ...prev[lid], amount: val } }));
    setPreview(null);
  };

  const buildLineItemsPayload = () =>
    Object.entries(selection)
      .filter(([, v]) => v.selected)
      .map(([lid, v]) => ({
        line_item_id: lid,
        quantity: v.quantity,
        restock_type: v.restock_type,
        ...(v.amount ? { amount: v.amount } : {}),
      }));

  const handlePreview = async () => {
    setPreviewing(true);
    setPreviewError(null);
    setPreview(null);
    try {
      const resp = await apiCall(
        `/api/admin/refunds/orders/${order.id}/preview`,
        {
          method: 'POST',
          body: JSON.stringify({
            line_items: buildLineItemsPayload(),
            shipping_mode: shippingMode,
            shipping_amount: shippingMode === 'custom' ? shippingCustomAmount : undefined,
            notify,
          }),
        }
      );
      setPreview(resp);
    } catch (err) {
      setPreviewError(err?.message || 'Preview failed.');
    } finally {
      setPreviewing(false);
    }
  };

  const handleExecute = async () => {
    setExecuting(true);
    try {
      // Re-auth gate: verify password against the auth endpoint
      // before issuing the destructive call.
      const reauth = await apiCall('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email: user?.email, password: reauthPwd }),
      }).catch(() => null);
      if (!reauth?.access_token) {
        toast({
          title: 'Re-authentication failed',
          description: 'Password incorrect. The refund was NOT executed.',
          variant: 'destructive',
        });
        setExecuting(false);
        return;
      }
      const resp = await apiCall(
        `/api/admin/refunds/orders/${order.id}/execute`,
        {
          method: 'POST',
          body: JSON.stringify({
            line_items: buildLineItemsPayload(),
            shipping_mode: shippingMode,
            shipping_amount: shippingMode === 'custom' ? shippingCustomAmount : undefined,
            notify,
            reverse_vendor_payout: reversePayout,
            reason: reason.trim(),
            expected_total_inr: preview?.preview_total_inr,
          }),
        }
      );
      setExecuteResult(resp);
      if (resp?.alert) {
        toast({
          title: '⚠️ Refund partially completed',
          description:
            'Shopify refunded successfully, but Razorpay reported an error. Check Alerts tab.',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Refund successful',
          description: `Refunded ₹${resp?.amount_inr}. ${
            resp?.auto_cancelled ? 'Order also cancelled.' : ''
          }`,
        });
      }
      if (typeof onRefunded === 'function') onRefunded(resp);
    } catch (err) {
      toast({
        title: 'Refund failed',
        description: err?.message || 'See server logs.',
        variant: 'destructive',
      });
    } finally {
      setExecuting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="refund-modal"
      >
        <DialogHeader>
          <DialogTitle>Refund Order {order?.name || order?.order_number}</DialogTitle>
        </DialogHeader>

        {/* ─────── If we already executed, show the result panel ─────── */}
        {executeResult ? (
          <div className="space-y-3 py-4" data-testid="refund-result">
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle2 className="h-5 w-5" />
              <span className="font-medium">Refund processed</span>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1">
              <div>Shopify refund id: <code>{executeResult.shopify_refund_id}</code></div>
              <div>Amount refunded: <span className="font-semibold">₹{executeResult.amount_inr}</span></div>
              <div>Razorpay: {executeResult.razorpay?.status} {executeResult.razorpay?.razorpay_refund_id && (
                <code className="text-xs">{executeResult.razorpay.razorpay_refund_id}</code>
              )}</div>
              <div>Payout reversal: {executeResult.payout_reversal?.reversed ?? 0} reversed,
                {' '}{executeResult.payout_reversal?.skipped_unrelated ?? 0} unrelated</div>
              <div>Auto-cancelled order: {executeResult.auto_cancelled ? 'Yes' : 'No'}</div>
            </div>
            {executeResult.alert && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm" data-testid="refund-alert">
                <div className="flex items-center gap-2 text-red-700 font-medium">
                  <AlertTriangle className="h-4 w-4" /> Razorpay desync alert raised
                </div>
                <div className="mt-1 text-red-700">
                  Shopify state moved forward but Razorpay reported an error.
                  Check <code>system_alerts</code> and retry the customer refund
                  manually if needed.
                </div>
              </div>
            )}
            {/* iter-232 Phase 9 — When the broad refund returns
                `needs_manual_reversal` / multi_vendor_partial, surface a
                CTA that opens the per-vendor Transfer Reversal dialog. */}
            {(executeResult.razorpay?.status === 'needs_manual_reversal'
              || executeResult.razorpay?.error_type === 'multi_vendor_partial') && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm space-y-2"
                   data-testid="refund-needs-manual-reversal">
                <div className="flex items-center gap-2 text-amber-800 font-medium">
                  <AlertTriangle className="h-4 w-4" />
                  Per-vendor Transfer Reversal required
                </div>
                <div className="text-amber-800">
                  This is a multi-vendor partial refund. Razorpay can&apos;t
                  auto-decide which vendor&apos;s transfer to reverse — pick
                  the vendor(s) below and reverse their share manually.
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setMvrOpen(true)}
                  data-testid="refund-open-mvr-btn"
                >
                  Open per-vendor reversal
                </Button>
              </div>
            )}
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)} data-testid="refund-close-btn">
                Close
              </Button>
            </DialogFooter>
          </div>
        ) : (
          /* ─────── Active refund form ─────── */
          <div className="space-y-4 py-2">
            {/* Lines, grouped by vendor */}
            {Object.entries(groupedByVendor).map(([vendor, vlines]) => (
              <div key={vendor} className="border rounded-lg p-3">
                <div className="text-xs font-medium text-gray-500 mb-2">
                  Sold by: {vendor}
                </div>
                {vlines.map((li) => {
                  const lid = String(li.shopify_line_item_id || li.id || li.line_item_id);
                  const row = selection[lid] || { selected: false, quantity: 1, amount: '' };
                  const originalQty = li.quantity || 1;
                  const unitPrice = parseFloat(li.price || li.unit_price || 0);
                  const maxAmount = (unitPrice * originalQty).toFixed(2);
                  return (
                    <div key={lid} className="py-2 border-b last:border-b-0">
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={row.selected}
                          onCheckedChange={() => toggleLine(lid)}
                          data-testid={`refund-line-${lid}-checkbox`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">
                            {li.title || li.name || 'Item'}
                          </div>
                          <div className="text-xs text-gray-500">
                            ₹{unitPrice.toFixed(2)} × {originalQty}
                            {li.selling_plan_allocation && (
                              <Badge variant="outline" className="ml-2 text-[10px]">Subscription</Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-gray-400">qty</span>
                          <Input
                            type="number"
                            min={1}
                            max={originalQty}
                            value={row.quantity}
                            onChange={(e) => setQty(lid, e.target.value)}
                            disabled={!row.selected}
                            className="w-14 h-7 text-sm"
                            data-testid={`refund-line-${lid}-qty`}
                          />
                        </div>
                      </div>
                      {/* Amount input — visible only when line is selected */}
                      {row.selected && (
                        <div className="flex items-center gap-2 mt-2 ml-7">
                          <span className="text-xs text-gray-500 whitespace-nowrap">Refund ₹</span>
                          <Input
                            type="number"
                            min={0.01}
                            max={maxAmount}
                            step={0.01}
                            value={row.amount}
                            onChange={(e) => setAmount(lid, e.target.value)}
                            className="h-7 text-sm w-32"
                            placeholder={maxAmount}
                            data-testid={`refund-line-${lid}-amount`}
                          />
                          <span className="text-xs text-gray-400">
                            (max ₹{maxAmount})
                          </span>
                          {row.amount && parseFloat(row.amount) < parseFloat(maxAmount) && (
                            <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-300">
                              partial
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}

            {/* Shipping mode */}
            <div className="space-y-1">
              <div className="text-sm font-medium">Shipping refund</div>
              <div className="flex flex-wrap gap-3 text-sm">
                {['full', 'proportional', 'none', 'custom'].map((m) => (
                  <label key={m} className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      name="shipping-mode"
                      value={m}
                      checked={shippingMode === m}
                      onChange={(e) => { setShippingMode(e.target.value); setPreview(null); }}
                      data-testid={`refund-shipping-${m}`}
                    />
                    <span className="capitalize">{m === 'custom' ? 'Custom ₹' : m}</span>
                  </label>
                ))}
              </div>
              {shippingMode === 'custom' && (
                <div className="flex items-center gap-2 mt-1.5">
                  <span className="text-xs text-gray-500">Shipping amount ₹</span>
                  <Input
                    type="number"
                    min={0}
                    step={0.01}
                    value={shippingCustomAmount}
                    onChange={(e) => { setShippingCustomAmount(e.target.value); setPreview(null); }}
                    className="h-7 text-sm w-28"
                    placeholder="0.00"
                    data-testid="refund-shipping-custom-amount"
                  />
                </div>
              )}
            </div>

            {/* Reason */}
            <div className="space-y-1">
              <div className="text-sm font-medium">Reason (≥20 characters, required)</div>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Customer reported quality issue; partial refund agreed."
                rows={2}
                className={reason.length > 0 && reason.trim().length < 20 ? 'border-amber-400' : ''}
                data-testid="refund-reason"
              />
              <div
                className={`text-xs font-medium ${reason.trim().length >= 20 ? 'text-green-600' : 'text-amber-600'}`}
                data-testid="refund-reason-counter"
              >
                {reason.trim().length >= 20 ? '✓ ' : ''}{reason.trim().length}/20 minimum
              </div>
            </div>

            {/* Options */}
            <div className="flex flex-wrap gap-4 text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={notify}
                  onCheckedChange={(v) => setNotify(!!v)}
                  data-testid="refund-notify"
                />
                Notify customer
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={reversePayout}
                  onCheckedChange={(v) => setReversePayout(!!v)}
                  data-testid="refund-reverse-payout"
                />
                Reverse vendor payout (Route)
              </label>
            </div>

            {/* Preview block */}
            {previewError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm flex gap-2 items-start" data-testid="refund-preview-error">
                <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-medium text-red-700">Preview failed</div>
                  <div className="text-red-600 text-xs">{previewError}</div>
                </div>
              </div>
            )}
            {preview && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm" data-testid="refund-preview-card">
                <div className="flex items-center gap-2 text-blue-700 font-medium mb-2">
                  <Info className="h-4 w-4" /> Preview from Shopify
                </div>
                <div className="space-y-0.5 font-mono text-xs">
                  {(preview.calculate?.refund_line_items || []).map((rli, i) => (
                    <div key={i} className="flex justify-between">
                      <span className="truncate pr-2">
                        Line {rli.line_item_id} × {rli.quantity}
                      </span>
                      <span>₹{rli.subtotal_set?.shop_money?.amount || rli.subtotal || '—'}</span>
                    </div>
                  ))}
                  <div className="border-t border-blue-200 mt-1 pt-1 flex justify-between font-bold">
                    <span>TOTAL</span>
                    <span>₹{preview.preview_total_inr}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Re-auth gate (only after preview) */}
            {preview && (
              <div className="space-y-1 border-t pt-3">
                <div className="text-sm font-medium flex items-center gap-1.5">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  Confirm with your password
                </div>
                <Input
                  type="password"
                  value={reauthPwd}
                  onChange={(e) => setReauthPwd(e.target.value)}
                  placeholder="Re-enter your admin password"
                  autoComplete="current-password"
                  data-testid="refund-reauth-password"
                />
                {executeBlockers.length > 0 && (
                  <ul className="text-xs text-amber-700 space-y-0.5 pt-1" data-testid="refund-execute-blockers">
                    {executeBlockers.map((msg) => (
                      <li key={msg}>• {msg}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                data-testid="refund-cancel-btn"
              >
                Cancel
              </Button>
              {!preview ? (
                <Button
                  onClick={handlePreview}
                  disabled={!canPreview || previewing}
                  data-testid="refund-preview-btn"
                >
                  {previewing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Preview refund
                </Button>
              ) : (
                <Button
                  onClick={handleExecute}
                  disabled={!canExecute}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="refund-execute-btn"
                >
                  {executing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Refund ₹{preview.preview_total_inr}
                </Button>
              )}
            </DialogFooter>
          </div>
        )}
      </DialogContent>
      {/* iter-232 Phase 9 — Per-vendor reversal sub-dialog,
          mounted alongside the parent dialog (not nested in the same
          DialogContent so both can show side-by-side if needed). */}
      <MultiVendorReversalDialog
        order={order}
        open={mvrOpen}
        onOpenChange={setMvrOpen}
        onDone={() => {
          // Refresh parent after a successful reversal
          if (typeof onRefunded === 'function') onRefunded(executeResult);
        }}
      />
    </Dialog>
  );
};

export default RefundModal;
